import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { loadSubscriptions } from '../redux/actions';
/**
 * The `useSubscriptions` hook returns an object representing the current user's
 * subscriptions.
 *
 * This hook has a side effect that will load all the user's subscriptions upon
 * calling it, if not already loaded.
 *
 * @example
 *
 * ```javascript
 * import { useSubscriptions } from '@chordcommerce/react-autonomy'
 *
 * const SubscriptionsPage = () => {
 *   const { subscriptions, isLoaded, isFetching, error } = useSubscriptions()
 *
 *   if (isFetching) {
 *     return <p>Loading…</p>
 *   }
 *
 *   return (
 *     <dl>
 *       {subscriptions.map(subscription => (
 *         <React.Fragment key={subscription.id}>
 *           <dt>Subscription #{subscription.id}</dt>
 *           <dd>
 *             Renews every {subscription.intervalLength} {subscription.intervalUnit}
 *             <br />
 *             (Items: {subscription.lineItems.map(li => li.sku).join(', ')})
 *           </dd>
 *         </React.Fragment>
 *       )}
 *     </dl>
 *   )
 * }
 * ```
 *
 * @category Hook
 */
export function useSubscriptions() {
  var _subscriptionsState$l, _subscriptionsState$s, _subscriptionsState$e;
  var subscriptionsState = useSelector(function (state) {
    return state.subscriptions;
  }, shallowEqual);
  var isFetching = !!((_subscriptionsState$l = subscriptionsState.loading) !== null && _subscriptionsState$l !== void 0 && _subscriptionsState$l.includes('list'));
  var isLoaded = !!((_subscriptionsState$s = subscriptionsState.success) !== null && _subscriptionsState$s !== void 0 && _subscriptionsState$s.includes('list'));
  var error = (_subscriptionsState$e = subscriptionsState.error) === null || _subscriptionsState$e === void 0 ? void 0 : _subscriptionsState$e.list;
  var dispatch = useDispatch();
  var actionDispatches = useMemo(function () {
    return bindActionCreators({
      loadSubscriptions: loadSubscriptions
    }, dispatch);
  }, [dispatch]);
  useEffect(function () {
    if (isFetching || isLoaded || error) return;
    if (Object.keys(subscriptionsState.data).length === 0) {
      actionDispatches.loadSubscriptions();
    }
  }, [subscriptionsState]);
  var flatSubscriptions = useMemo(function () {
    return Object.values(subscriptionsState.data);
  }, [subscriptionsState.data]);
  return _objectSpread({
    subscriptions: flatSubscriptions,
    isLoaded: isLoaded,
    isFetching: isFetching,
    error: error
  }, actionDispatches);
}